import * as React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Drawer } from '@material-tailwind/react';
import { LogOut, Menu as MenuIcon, X } from 'lucide-react';
import { ProtectedRoutes, URLS } from '../router-x/routes';
import { useAuth } from '../services/contexts/authContext';
import { useGetUserNotifications } from '../custom-hooks/use-notifications';
import ImpersonationBanner from './impersonation-banner';
import PulseWaveLogo from '../assets/svg-pulse-wave-logo';

// Nav items that display an unread-notifications badge (parity with the admin shell).
const NOTIF_BADGE_IDS = ['FX7-1', 'FX18-2', 'FX20-1', 'FX25'];
// Preferred display order for the portal sidebar (matches the Pulse design).
const NAV_ORDER = ['Home', 'Notifications', 'Profile', 'Meeting Rooms', 'Contracts', 'Support', 'Manage Team'];

// Portal shell for customer (USER) and team-member roles.
// Uses the same route-driven RBAC as the admin shell, restyled to the Pulse design system.
// Note: there is no global page header — the greeting/bell/avatar live on the Home page only.
const PortalShell = ({ children }) => {
	const location = useLocation();
	const navigate = useNavigate();
	const [open, setOpen] = React.useState(false);
	const { userDetails, logout } = useAuth();

	const closeDrawer = () => setOpen(false);
	const openDrawer = () => setOpen(true);

	const { data: notificationsData } = useGetUserNotifications();
	const userNotificationsCount = React.useMemo(() => {
		const list = notificationsData?.notifications || [];
		return list.filter((n) => !n.readAt).length;
	}, [notificationsData]);

	const handleLogout = async () => {
		try {
			const result = await logout();
			if (result?.wasImpersonating) {
				navigate(result.returnPath || URLS.ADMIN.ROOT);
			}
			// Normal logout — onAuthStateChanged will redirect to /auth
		} catch (err) {
			console.error('Logout failed:', err);
		}
	};

	// Same access logic as the admin shell so per-role nav items stay correct.
	const hasRouteAccess = (route) => {
		if (!route.inSideBarList) return false;
		if (userDetails.role === 'super_admin') return true;
		if (userDetails.role === 'staff') {
			const permissions = userDetails?.dynamicRole?.permissions || [];
			return permissions.includes(route.id);
		}
		let hasRoleAccess = route.permissibleRoles.includes(userDetails.role);
		if (hasRoleAccess && route.requiredAllowedPage) {
			const userAllowedPages = userDetails?.allowedPages || [];
			hasRoleAccess = userAllowedPages.includes(route.requiredAllowedPage);
		}
		return hasRoleAccess;
	};

	const orderIndex = (name) => {
		const i = NAV_ORDER.indexOf(name);
		return i === -1 ? NAV_ORDER.length : i;
	};

	const navRoutes = ProtectedRoutes
		.filter((r) => r.group === null && hasRouteAccess(r))
		.sort((a, b) => orderIndex(a.name) - orderIndex(b.name));

	const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

	const renderSidebar = (showClose = false) => (
		<div className="flex h-full flex-col">
			<div className="flex items-center gap-3 px-2 pb-4">
				<PulseWaveLogo width={56} height={28} />
				<div className="flex-1">
					<div className="font-display text-lg font-bold leading-none text-ink">Pulse</div>
					<div className="mt-0.5 text-[11px] text-brand">Project of Workbench</div>
				</div>
				{showClose && (
					<button
						onClick={closeDrawer}
						aria-label="Close menu"
						className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] text-[#6B675F] transition-colors hover:bg-cream-input"
					>
						<X size={20} />
					</button>
				)}
			</div>

			<nav className="mt-1 flex flex-1 flex-col gap-0.5 overflow-y-auto">
				{navRoutes.map((route) => {
					const active = isActive(route.path);
					const showBadge = NOTIF_BADGE_IDS.includes(route.id) && userNotificationsCount > 0;
					return (
						<Link
							key={route.id}
							to={route.path}
							onClick={closeDrawer}
							className={`flex items-center gap-3 rounded-[11px] px-3.5 py-2.5 text-sm outline-none transition-colors ${
								active
									? 'bg-cream-input font-semibold text-brand'
									: 'font-medium text-[#6B675F] hover:bg-[#F3EEE6] hover:text-ink'
							}`}
						>
							<span className="shrink-0">
								{route.icon ? React.cloneElement(route.icon, { size: 20 }) : null}
							</span>
							<span className="flex-1">{route.name}</span>
							{showBadge && (
								<span className="ml-auto rounded-full bg-[#FBEFD4] px-2 py-0.5 text-[11px] font-bold text-[#B07A12]">
									{userNotificationsCount}
								</span>
							)}
						</Link>
					);
				})}
			</nav>

			<div className="mt-auto border-t border-[#ECE3D5] pt-3">
				<button
					onClick={handleLogout}
					className="flex w-full items-center gap-3 rounded-[11px] px-3.5 py-2.5 text-sm font-medium text-[#6B675F] outline-none transition-colors hover:bg-[#FAE6E1] hover:text-[#BE3B28]"
				>
					<LogOut size={20} />
					Logout
				</button>
			</div>
		</div>
	);

	return (
		<React.Fragment>
			<ImpersonationBanner />
			<div className="flex min-h-screen bg-cream">
				{/* Desktop sidebar */}
				<aside className="sticky top-0 hidden h-screen w-[248px] flex-shrink-0 flex-col border-r border-[#ECE3D5] bg-white px-4 py-6 md:flex">
					{renderSidebar()}
				</aside>

				{/* Main column */}
				<div className="flex min-w-0 flex-1 flex-col">
					{/* Mobile top bar (hamburger + brand) — mobile only, no global header on desktop */}
					<header className="sticky top-0 z-40 flex h-16 items-center justify-between gap-3 border-b border-[#ECE3D5] bg-cream/95 px-4 backdrop-blur md:hidden">
						<div className="flex items-center gap-2">
							<PulseWaveLogo width={40} height={20} />
							<span className="font-display text-base font-bold text-ink">Pulse</span>
						</div>
						<button
							onClick={openDrawer}
							aria-label="Open menu"
							className="flex h-10 w-10 items-center justify-center rounded-[11px] border border-[#ECE3D5] bg-white text-[#6B675F] transition-colors hover:bg-cream-input"
						>
							<MenuIcon size={20} />
						</button>
					</header>

					<main className="flex-1">{children}</main>
				</div>
			</div>

			{/* Mobile drawer */}
			<Drawer open={open} onClose={closeDrawer} className="p-0">
				<div className="h-full bg-white px-4 py-6">
					{renderSidebar(true)}
				</div>
			</Drawer>
		</React.Fragment>
	);
};

export default PortalShell;
